import React, { useEffect, useState } from "react";
import { useCombobox } from "downshift";
import { getString, getSuggestions } from "../../utilities";
import ChevronDown from "../../../assets/svg/chevron-down.svg";
import ChevronUp from "../../../assets/svg/chevron-up.svg";

export default function Select({
  error,
  items,
  label,
  onChange,
  onBlur,
  placeholder = getString("forms.select.0"),
  searchKey,
  defaultValue,
  dataTestId,
}) {
  const validationErrorId = error ? `${error.ref.name}-validation-error` : "";

  const dataTestIdProps = dataTestId ? { "data-testid": dataTestId } : {};

  // Preprocess items to add a unique id to each item.
  const processedItems = items.map((item, index) => {
    return { ...item, id: `${item[searchKey]}-${index}` };
  });

  const [suggestions, setSuggestions] = useState(processedItems);

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getToggleButtonProps,
    getItemProps,
    highlightedIndex,
    setHighlightedIndex,
    closeMenu,
  } = useCombobox({
    items: suggestions,
    onSelectedItemChange: ({ selectedItem }) => {
      onChange(selectedItem);
      closeMenu();
    },
    onInputValueChange: ({ inputValue }) => {
      if (inputValue.length === 0) {
        setSuggestions(processedItems);
      } else {
        const newSuggestions = getSuggestions(inputValue, processedItems, searchKey);
        setSuggestions(newSuggestions);
      }
    },
    itemToString: (item) => (item ? item[searchKey] : ""),
    initialSelectedItem: defaultValue,
  });

  useEffect(() => {
    setSuggestions(processedItems);
  }, [items]);

  useEffect(() => {
    if (highlightedIndex <= -1) {
      setHighlightedIndex(0);
    }
  }, [isOpen, suggestions]);

  const renderChevron = () => {
    if (isOpen) {
      return <ChevronUp />;
    }
    return <ChevronDown />;
  };

  const describedByProp = error ? { "aria-describedby": validationErrorId } : {};
  const wrapperStylesArray = ["form-field", "form-field-select"];
  const labelStylesArray = ["static-label"];

  if (error) {
    wrapperStylesArray.push("form-field-error");
    labelStylesArray.push("static-label-error");
  }

  const renderLabel = () => {
    if (label) {
      return (
        <div className={labelStylesArray.join(" ")} {...getLabelProps()}>
          {label}
        </div>
      );
    }

    return null;
  };

  const handleComponentBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      // don't trigger onBlur when focus moves within the Select
      return;
    }

    onBlur?.(event);
  };

  return (
    <>
      {renderLabel()}
      <div className={wrapperStylesArray.join(" ")} onBlur={handleComponentBlur}>
        <div className="form-field-select-container">
          <input placeholder={placeholder} {...getInputProps()} {...describedByProp} />

          <ul {...getMenuProps()} className="select-suggestions-container suggestions-list">
            {isOpen &&
              suggestions.map((item, index) => {
                return (
                  <li
                    {...dataTestIdProps}
                    className={`suggestion${highlightedIndex === index ? " highlighted" : ""}`}
                    key={item.id}
                    {...getItemProps({ item, index })}
                  >
                    {item[searchKey]}
                  </li>
                );
              })}
          </ul>
          <button type="button" className="select-toggle" {...getToggleButtonProps()}>
            {renderChevron()}
          </button>
        </div>
        {error && (
          <div className="validation-error" id={validationErrorId} role="alert" aria-live="polite">
            {error.message}
          </div>
        )}
      </div>
    </>
  );
}
